import React, { useState } from "react";
import { Card, CardContent, Typography, Stack, Box, Skeleton, Avatar } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { ReactComponent as Donut } from '../../../../assets/images/donut.svg'; // Keep as component import

// Custom Tooltip Component (unchanged)
const CustomTooltip = ({ visible, position, data, title }) => {
  if (!visible || !data) return null;
  console.log('Tooltip Data:', data);
  return (
    <Box
      sx={{
        position: 'fixed',
        left: position?.x || 0,
        top: position?.y || 0,
        width: '168px',
        height: '113px',
        flexShrink: 0,
        borderRadius: '8px',
        border: '1px solid #D5DCE1',
        background: '#FFF',
        boxShadow: '0 15px 20px 0 rgba(6, 21, 34, 0.05)',
        padding: '12px',
        zIndex: 1000,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Typography
        sx={{
          color: '#061522',
          fontFamily: 'Inter',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '18px',
          marginBottom: '16px',
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          color: '#405261',
          fontFamily: 'Inter',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '18px',
          marginBottom: '4px',
        }}
      >
        {data.name}
      </Typography>
      <Typography
        sx={{
          color: '#061522',
          fontFamily: 'Inter',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '18px',
        }}
      >
        {data.count !== undefined && data.count !== null ? (
          <>
            <span>{data.count}</span>
            {data.value !== undefined && data.value !== null && (
              <span> ({data.value}%)</span>
            )}
          </>
        ) : (
          data.value !== undefined && data.value !== null && (
            <span>{data.value}%</span>
          )
        )}
      </Typography>
    </Box>
  );
};

export default function DonutChart({ title, description, data, loading = false }) {
  const [tooltip, setTooltip] = useState({ visible: false, position: null, data: null });
  const tooltipTimeoutRef = React.useRef(null);

  const processedData = data && data.length > 0 ? data : [
    { name: 'Positive', value: 0, color: '#52C41A' },
    { name: 'Negative', value: 0, color: '#FF4D4F' }
  ];

  const totalValue = processedData.reduce((sum, item) => sum + item.value, 0);
  const chartData = totalValue === 0
    ? processedData.map(item => ({ ...item, value: 1 }))
    : processedData;

  const handleMouseEnter = (chartItem, index, event) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }

    if (event && event.target && event.clientX) {
      const originalData = processedData[index];

      // Position tooltip to the right of cursor, vertically centered with cursor
      const tooltipX = event.clientX + 20;
      const tooltipY = event.clientY - 56; // Center the tooltip vertically (113px height / 2)

      setTooltip({
        visible: true,
        position: {
          x: tooltipX,
          y: tooltipY
        },
        data: originalData
      });
    }
  };

  const handleMouseLeave = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltip({ visible: false, position: null, data: null });
    }, 50);
  };

  React.useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "8px",
        border: "1px solid #E5E9EC",
        background: "#FFF",
        width: '100%',
        minWidth: '544px',
        position: 'relative',
      }}
      onMouseLeave={handleMouseLeave}
    >
      <CardContent sx={{ p: '24px' }}>
        <Typography
          sx={{
            color: "#061522",
            fontFamily: "Inter, sans-serif",
            fontSize: "18px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "18px",
            mb: 1,
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            sx={{
              color: "#405261",
              fontFamily: "Inter, sans-serif",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "16px",
              mb: 2,
            }}
          >
            {description}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ width: 304, height: 304, mx: "auto", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Skeleton variant="circular" width={280} height={280} />
          </Box>
        ) : totalValue === 0 ? (
          <Box sx={{ width: 304, height: 304, mx: "auto", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {typeof Donut === 'function' ? (
              <Avatar
                sx={{
                  width: 304,
                  height: 304,
                  bgcolor: 'transparent', // Avoid default background fill
                }}
                alt="Empty Donut Chart"
              >
                <Donut /> {/* Render as child component */}
              </Avatar>
            ) : (
              <Typography color="textSecondary">No data or image available</Typography> // Fallback if import fails
            )}
          </Box>
        ) : (
          <Box
            sx={{
              width: 304, height: 304, mx: "auto", '& *': {
                outline: 'none !important'
              },
              '& *:focus': {
                outline: 'none !important'
              }
            }}
            onMouseLeave={handleMouseLeave}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart onMouseLeave={handleMouseLeave}>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={90}
                  outerRadius={140}
                  paddingAngle={2}
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive={false}
                  stroke="none"
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.color}
                      stroke="none"
                      style={{ cursor: 'pointer', outline: 'none' }}
                      onMouseEnter={(event) => handleMouseEnter(entry, index, event)}
                      onMouseLeave={handleMouseLeave}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Box>
        )}

        {loading ? (
          <Stack direction="row" spacing={3} justifyContent="center" sx={{ mt: 1 }}>
            <Skeleton variant="rectangular" width={80} height={20} />
            <Skeleton variant="rectangular" width={80} height={20} />
          </Stack>
        ) : (
          <Stack direction="row" spacing={3} justifyContent="center" sx={{ mt: '32px' }}>
            {processedData.map((d) => (
              <Stack key={d.name} direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: d.color,
                    flexShrink: 0
                  }}
                />
                <Typography
                  sx={{
                    color: "#405261",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "18px",
                  }}
                >
                  {d.name}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>

      <CustomTooltip
        title={title}
        visible={tooltip.visible}
        position={tooltip.position}
        data={tooltip.data}
      />
    </Card>
  );
}